import { z } from 'zod'
import { UserOrgWithOrgDtoSchema } from '../../user-org/models/UserOrgWithOrgDto'
import UserEntityDtoSchema from './UserEntityDtoSchema'
import { NotificationDto } from '../../notification/models/NotificationDto'
import OrgEntityDtoSchema from '../../org/models/OrgEntityDtoSchema'
import { RoleEntityDtoSchema } from '../../role/models/RoleEntityDto'
import UserOrgEntityDtoSchema from '../../user-org/models/UserOrgEntityDto'
import { superRefinePassword } from '@gap/backend/util/validations/validation-passwords'
import errorMap from '@gap/backend/util/validations/validation-error-map'
import { UserOrgDto } from '../../user-org/models/UserOrgDto'
import { UserOrgUserTypeSchema } from '../../user-org/models/UserOrgUserTypeEnum'

export namespace UserDto {
   export const entitySchema = UserEntityDtoSchema
   export type entity = z.output<typeof UserEntityDtoSchema>

   export const withOrgsSchema = UserEntityDtoSchema.pick({
      id: true,
      cognitoUsername: true,
      email: true,
      firstName: true,
      lastName: true,
      jobTitle: true,
      onboarding: true,
      trialData: true,
      inviteAcceptedOn: true,
      circleId: true,
   }).merge(
      z.object({
         isGapUser: z.boolean().optional(),
         orgs: z.array(UserOrgWithOrgDtoSchema).optional(),
      })
   )
   export type withOrgs = z.output<typeof withOrgsSchema>

   export const createTrialUserSchema = withOrgsSchema
      .omit({
         id: true,
         orgs: true,
         isGapUser: true,
         cognitoUsername: true,
      })
      .merge(
         z.object({
            invitationEmail: UserEntityDtoSchema.shape.email.optional(),
         })
      )
   export type CreateTrialUser = z.output<typeof createTrialUserSchema>

   export const minimalSchema = withOrgsSchema
      .pick({
         id: true,
         firstName: true,
         lastName: true,
         email: true,
      })
      .merge(
         z.object({
            deactivatedOn: UserOrgEntityDtoSchema.shape.deactivatedOn.optional(),
         })
      )
   export type minimal = z.output<typeof minimalSchema>

   export const profileSchema = UserEntityDtoSchema.pick({ firstName: true, lastName: true })
   export type Profile = z.output<typeof profileSchema>

   export const profileAdvisorSchema = profileSchema.merge(UserEntityDtoSchema.pick({ jobTitle: true }))
   export type ProfileAdvisor = z.output<typeof profileAdvisorSchema>

   export const onboardingSchema = UserEntityDtoSchema.shape.onboarding
   export type Onboarding = z.output<typeof onboardingSchema>

   /** Per-user calendar preferences: the Meetings-list upcoming day-limit and Scribe auto-join. */
   export const calendarPreferencesSchema = UserEntityDtoSchema.shape.calendarPreferences.unwrap()
   export type CalendarPreferences = z.output<typeof calendarPreferencesSchema>

   export type info = UserDto.withOrgs & {
      userOrg: UserOrgDto.Info
      notifications: NotificationDto[]
      circleEnabled?: boolean
   }

   export const updateEmailSchema = z.object({
      currentEmail: UserEntityDtoSchema.shape.email,
      newEmail: UserEntityDtoSchema.shape.email,
   })
   export type UpdateEmail = z.output<typeof updateEmailSchema>

   export const updateFromAmplifySchema = UserEntityDtoSchema.pick({ jobTitle: true, email: true, cognitoUsername: true }).merge(
      z.object({
         firstName: UserEntityDtoSchema.shape.firstName.optional(),
         lastName: UserEntityDtoSchema.shape.lastName.optional(),
      })
   )
   export type UpdateFromAmplify = z.output<typeof updateFromAmplifySchema>

   export const updateFieldsSchema = UserEntityDtoSchema.pick({ email: true, firstName: true, lastName: true }).merge(
      z.object({
         orgId: OrgEntityDtoSchema.shape.id,
         roleId: RoleEntityDtoSchema.shape.id.refine(value => !!value, {
            message: 'Role ID is required',
         }),
         userType: UserOrgUserTypeSchema,
      })
   )
   export type UpdateFields = z.output<typeof updateFieldsSchema>

   export const createAdvisorSchema = updateFieldsSchema
   export const createNonAdvisorSchema = updateFieldsSchema
   export type CreateAdvisor = z.output<typeof createAdvisorSchema>
   export type CreateNonAdvisor = z.output<typeof createNonAdvisorSchema>

   export const updateAdvisorSchema = updateFieldsSchema.merge(UserEntityDtoSchema.pick({ id: true }))
   export type UpdateAdvisor = z.output<typeof updateAdvisorSchema>

   export const updateNonAdvisorSchema = updateAdvisorSchema.merge(
      z.object({
         originalRoleId: RoleEntityDtoSchema.shape.id.refine(value => !!value, {
            message: 'Must have at least 1 role previously assigned',
         }),
      })
   )
   export type UpdateNonAdvisor = z.output<typeof updateNonAdvisorSchema>

   // Only for the purpose of frontend validation
   export const createTrialUserValidationSchema = UserEntityDtoSchema.pick({
      firstName: true,
      lastName: true,
      email: true,
   })
      .merge(
         z.object({
            password: z.string({ errorMap }).superRefine(superRefinePassword),
         })
      )

   export const listSchema = UserEntityDtoSchema.pick({
      id: true,
      firstName: true,
      lastName: true,
      jobTitle: true,
      email: true,
   }).merge(
      z.object({
         roleId: RoleEntityDtoSchema.shape.id.optional(),
         roleName: RoleEntityDtoSchema.shape.name.optional(),
         roleDescription: RoleEntityDtoSchema.shape.description.optional(),
      })
   )
   export type List = z.output<typeof listSchema>

   export const listWithRegisteredSchema = listSchema
      .extend({
         isRegistered: z.boolean(),
         createdAt: UserOrgEntityDtoSchema.shape.createdAt.optional(),
         inviteSentOn: UserOrgEntityDtoSchema.shape.inviteSentOn.optional(),
         deactivatedOn: UserOrgEntityDtoSchema.shape.deactivatedOn.optional(),
         userType: UserOrgUserTypeSchema.optional(),
      })
      .merge(
         UserEntityDtoSchema.pick({
            inviteAcceptedOn: true,
            circleId: true,
         })
      )
   export type ListWithRegistered = z.output<typeof listWithRegisteredSchema>
}
